import React, { useEffect, useRef, useState } from "react"
import { Input, InputProps, Alert, InputGroup } from "reactstrap"
import "./FloatingLabelInput.scss"
import cn from "classnames"
import $ from "jquery"

export interface IFloatingLabelsInputProps extends InputProps {
    placeholder?: string
    extraLabel?: string
    textareaNotExtendable?: boolean
    autoCompleteOff?: boolean
    disableHintAfter?: number
}

const selectors = {
    targetClass: "placeholder-hidden",
}

const handleValueChange = ($element: JQuery<HTMLElement>) => {
    if (!$element.hasClass(selectors.targetClass) && $element.val()) {
        $element.addClass(selectors.targetClass)
        $element.parent().find("span").addClass(selectors.targetClass)
    }
}

const applyListeners = ($element: JQuery<HTMLElement>) => {
    const handleGotFocus = () => {
        if (!$element.hasClass(selectors.targetClass)) {
            $element.addClass(selectors.targetClass)
            $element.parent().find("span.floating-label").addClass(selectors.targetClass)
        }
    }
    const handleLostFocus = () => {
        if ($element.hasClass(selectors.targetClass) && !$element.val()) {
            $element.removeClass(selectors.targetClass)
            $element.parent().find("span.floating-label").removeClass(selectors.targetClass)
        }
    }

    handleValueChange($element)

    $element.on("focusout", () => {
        handleLostFocus()
    })
    $element.on("focusin", () => {
        handleGotFocus()
    })
    $element.on("keydown", () => {
        handleGotFocus()
    })
    $element.on("input", () => {
        handleValueChange($element)
    })
}

const applyFloatingLabels = (node: HTMLDivElement) => {
    const inputs = $(node).find("input")
    inputs.each((index, element) => {
        applyListeners($(element))
    })

    const textareasExtendable = $(node).find("textarea.extendable")
    textareasExtendable.each((index, element) => {
        applyListeners($(element))
        element.addEventListener(
            "input",
            function () {
                this.style.overflow = "hidden"
                this.style.height = "0"
                this.style.height = this.scrollHeight + "px"
            },
            false
        )
    })

    const textareasUnextendable = $(node).find("textarea.unextendable")
    textareasUnextendable.each((index, element) => {
        applyListeners($(element))
    })
}

const FloatingLabelsInput = ({
    placeholder,
    extraLabel,
    bottomHint,
    type,
    textareaNotExtendable,
    onFocus,
    onBlur,
    onChange,
    autoCompleteOff,
    disableHintAfter,
    ...otherProps
}: IFloatingLabelsInputProps) => {
    const handle = useRef<HTMLDivElement | null | undefined>()
    const [initialized, setInitialized] = useState(false)
    const [showHint, setHint] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    const spanHandle = useRef<HTMLSpanElement | null | undefined>()
    useEffect(() => {
        if (handle.current) {
            applyFloatingLabels(handle.current)

            setTimeout(() => {
                // First animate placeholders after the initial setup
                // If input starts with a value, placeholder should float without an animation
                setInitialized(true)
            }, 100)
        }
    }, [])

    if (handle.current) {
        handleValueChange($(handle.current).find("input").eq(0))
    }

    return (
        <div ref={(ref) => (handle.current = ref)} className="form-label-group mb-0">
            {type === "password" ? (
                <InputGroup>
                    <Input
                        {...otherProps}
                        autoComplete={autoCompleteOff ? "new-password" : ""}
                        className="password-input"
                        type={showPassword ? "text" : "password"}
                        onFocus={onFocus ? onFocus : () => setHint(true)}
                        onBlur={onBlur ? onBlur : () => setHint(false)}
                    />
                    <span
                        className={cn(initialized && "initialized", "floating-label")}
                        ref={(ref) => (spanHandle.current = ref)}
                        data-value={placeholder}
                    ></span>
                    <div className="show-password" onClick={() => setShowPassword(!showPassword)}>
                        <span
                            className={cn("show-password-text", "no-select", "icon", showPassword ? "icon-hide" : "icon-show")}
                        ></span>
                    </div>
                </InputGroup>
            ) : (
                <InputGroup>
                    <Input
                        {...otherProps}
                        autoComplete={autoCompleteOff ? "new-password" : ""}
                        className={cn(textareaNotExtendable ? "unextendable" : "extendable")}
                        type={type || "text"}
                        onFocus={onFocus ? onFocus : () => setHint(true)}
                        onBlur={onBlur ? onBlur : () => setHint(false)}
                        onChange={(e) => {
                            if (disableHintAfter) {
                                e.target.value.length > disableHintAfter ? setHint(false) : setHint(true)
                            }
                            if (onChange) {
                                onChange(e)
                            }
                        }}
                    />
                    <span
                        className={cn(initialized && "initialized", "floating-label")}
                        ref={(ref) => (spanHandle.current = ref)}
                        data-value={placeholder}
                    ></span>
                </InputGroup>
            )}
            {extraLabel && <div className="extra-label">{extraLabel}</div>}
            <Hint hintText={bottomHint} showElement={showHint}></Hint>
        </div>
    )
}

const Hint = ({ hintText, showElement }: { hintText?: string; showElement: boolean }) => {
    return (
        <>
            {hintText && (
                <Alert isOpen={showElement} className="alert-f2" color="dark">
                    <span className={cn("icon page-icon icon-exclamation-mark")}></span> {hintText}
                </Alert>
            )}
        </>
    )
}

export default FloatingLabelsInput
